import { createContext, useState, useEffect } from "react";
import { api } from '../api';
import useCities from '../hooks/useCities';
import useCommunities from '../hooks/useCommunities';
import useProperties from '../hooks/useProperties';
import useFacilities from '../hooks/useFacilities';

export const MainContext = createContext(null);

export function MainContextProvider({ children }) {
    const [selectComActive, setSelectComActive] = useState(false);
    const [selectProActive, setSelectProActive] = useState(false);
    const [modal, setModal] = useState(false);
    const [btnActive, setBtnActive] = useState(false);
    const [selectActive, setSelectActive] = useState(false);
    const [communitiesMarkers, setCommunitiesMarkers] = useState(true);
    const [propertiesMarkers, setPropertiesMarkers] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [markers, setMarkers] = useState([]);
    const [showDistances, setShowDistances] = useState(false);
    const [groupModal, setGroupModal] = useState(false);

    // Use custom hooks
    const { cities: pages, loading: loadingCities } = useCities();
    const { communities: allCommunitiesRaw, loading: loadingCommunities } = useCommunities();
    const { properties: allPropertiesRaw, loading: loadingProperties } = useProperties();
    
    // Create state for processed data
    const [allCommunities, setAllCommunities] = useState([]);
    const [allProperties, setAllProperties] = useState([]);
    
    // Sort cities in the order: Dubai, RAK, Abu Dhabi
    const [sortedPages, setSortedPages] = useState([]);
    
    useEffect(() => {
        if (pages && pages.length > 0) {
            // Create a copy of pages to sort
            const sortedCities = [...pages];
            
            // Custom sort function for the specific order
            sortedCities.sort((a, b) => {
                // Define the order: Dubai first, RAK second, Abu Dhabi third
                const cityOrder = {
                    'dubai': 1,
                    'ras-al-khaimah': 2,
                    'abu-dhabi': 3
                };
                
                // Extract city paths from the full paths
                const pathA = a.path.split('/')[1] || '';
                const pathB = b.path.split('/')[1] || '';
                
                // Compare based on the defined order
                return (cityOrder[pathA] || 99) - (cityOrder[pathB] || 99);
            });
            
            setSortedPages(sortedCities);
        } else {
            setSortedPages([]);
        }
    }, [pages]);
    
    // Use the facilities hook without cityId to get all facilities
    const { facilities: allFacilities, loading: loadingFacilities } = useFacilities({ autoFetch: true });
    
    const [allLandmarks, setAllLandmarks] = useState([]);
    const [allMalls, setAllMalls] = useState([]);
    const [allSchools, setAllSchools] = useState([]);
    const [allHospitals, setAllHospitals] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [groupProperties, setGroupProperties] = useState([]);
    const [loading, setLoading] = useState(true);

    // Process facilities by type when they're loaded
    useEffect(() => {
        if (allFacilities && allFacilities.length > 0) {
            setAllLandmarks(allFacilities.filter(facility => facility.facility_type === 'landmark'));
            setAllMalls(allFacilities.filter(facility => facility.facility_type === 'mall'));
            setAllSchools(allFacilities.filter(facility => facility.facility_type === 'school'));
            setAllHospitals(allFacilities.filter(facility => facility.facility_type === 'hospital'));
        }
    }, [allFacilities]);

    // Process the properties data
    useEffect(() => {
        if (allPropertiesRaw.length > 0) {
            const processedProperties = allPropertiesRaw
                .filter(property => property.enabled === undefined || property.enabled === true)
                .map(property => ({
                    ...property,
                    city_path: property.path?.split("/")[1] || '' // Extract city_path from path
                }));
            setAllProperties(processedProperties);
        }
    }, [allPropertiesRaw]);

    // Process the communities data
    useEffect(() => {
        if (allCommunitiesRaw.length > 0) {
            const processedCommunities = allCommunitiesRaw.map(community => ({
                ...community,
                city_path: community.path?.split("/")[1] || '' // Extract city_path from path
            }));
            setAllCommunities(processedCommunities);
        }
    }, [allCommunitiesRaw]);

    // Fetch group markers
    useEffect(() => {
        const fetchGroupMarkers = async () => {
            try {
                const groupsResponse = await api.get('/group-markers/');
                
                // Ensure each group has modal_map_coords_pin property
                setAllGroups(groupsResponse.data.map(group => ({
                    ...group,
                    modal_map_coords_pin: group.modal_map_coords_pin || [],
                    // Normalize city_path by removing leading slash if present
                    city_path: group.city_path ? group.city_path.replace(/^\//, '') : (group.path?.split("/")[1] || '')
                })));
            } catch (error) {
                console.error("Error fetching groups:", error);
                setAllGroups([]);
            }
        };

        fetchGroupMarkers();
    }, []);

    // Fetch group properties to get their IDs
    useEffect(() => {
        const fetchGroupProperties = async () => {
            try {
                const response = await api.get('/group-properties/');
                setGroupProperties(response.data);
                console.log("Loaded group properties:", response.data.length);
            } catch (error) {
                console.error("Error fetching group properties:", error);
                setGroupProperties([]);
            }
        };

        fetchGroupProperties();
    }, []);

    // Update loading state based on all data loading states
    useEffect(() => {
        setLoading(loadingCities || loadingCommunities || loadingProperties || loadingFacilities);
    }, [loadingCities, loadingCommunities, loadingProperties, loadingFacilities]);

    const facilityMarkers = [
        { id: 'landmarks', markers: allLandmarks },
        { id: 'malls', markers: allMalls },
        { id: 'schools', markers: allSchools },
        { id: 'hospitals', markers: allHospitals },
    ];

    return (
        <MainContext.Provider
            value={{
                selectComActive,
                setSelectComActive,
                selectProActive,
                setSelectProActive,
                modal,
                setModal,
                btnActive,
                setBtnActive,
                selectActive,
                setSelectActive,
                communitiesMarkers,
                setCommunitiesMarkers,
                propertiesMarkers,
                setPropertiesMarkers,
                selectedItems,
                setSelectedItems,
                markers,
                setMarkers,
                allCommunities,
                allProperties,
                showDistances,
                setShowDistances,
                groupModal, 
                setGroupModal,
                allGroups,
                groupProperties,
                facilityMarkers,
                loading,
                pages: sortedPages
            }}
        >
            {children}
        </MainContext.Provider>
    );
}
